import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { BLOG_POSTS, BLOG_SITE_URL, buildBreadcrumbSchema } from '../data/blogPosts';

export default function BlogIndexPage() {
  const navigate = useNavigate();
  const canonical = `${BLOG_SITE_URL}/blog`;

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: BLOG_POSTS.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${BLOG_SITE_URL}/blog/${p.slug}`,
      name: p.title,
    })),
  };

  const breadcrumb = buildBreadcrumbSchema([
    { name: 'Home', url: `${BLOG_SITE_URL}/` },
    { name: 'Blog', url: canonical },
  ]);

  return (
    <>
      <Helmet>
        <title>Agent9 Blog — AI for Real Estate, Plumbing, and HVAC Businesses</title>
        <meta
          name="description"
          content="The Agent9 blog — practical guides on AI assistants for plumbers, HVAC businesses, real estate agents, and other service trades. Capture more leads and book more jobs."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Agent9 Blog — AI for Service Businesses" />
        <meta
          property="og:description"
          content="Practical guides on AI receptionists, scheduling automation, and lead capture for service businesses."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Agent9 Blog" />
        <meta
          name="twitter:description"
          content="AI for plumbers, HVAC businesses, real estate agents, and other service trades."
        />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <div className="min-h-screen bg-background text-foreground" data-testid="blog-index-page">
        <nav className="fixed top-0 w-full z-50 bg-card/80 backdrop-blur-xl border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/assets/agent9-logo.png" alt="Agent9" className="h-8 object-contain" />
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
              <Link to="/blog" className="text-foreground">Blog</Link>
              <Link to="/agent9" className="hover:text-foreground transition-colors">Agent9</Link>
              <Link to="/voicedeck" className="hover:text-foreground transition-colors">VoiceDeck</Link>
              <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle compact />
              <button
                onClick={() => navigate('/signup')}
                className="text-sm bg-foreground hover:bg-foreground/90 text-background px-4 py-2 rounded-full font-medium"
                data-testid="blog-index-signup"
              >
                Get Started
              </button>
            </div>
          </div>
        </nav>

        <header className="pt-28 pb-10">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              The Agent9 Blog
            </p>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-foreground">
              Practical AI for service businesses
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              How real estate agents, plumbers, HVAC businesses, and other service trades are using
              AI receptionists and scheduling automation to capture more leads and book more jobs.
            </p>
          </div>
        </header>

        <main className="pb-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-5" data-testid="blog-index-list">
            {BLOG_POSTS.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="block group rounded-2xl border border-border bg-card p-5 sm:p-7 transition-colors hover:border-foreground/30"
                data-testid={`blog-card-${post.slug}`}
              >
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-2">
                  <span className="inline-flex items-center gap-1">
                    <Calendar size={11} />
                    {new Date(post.publishedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock size={11} /> {post.readingMinutes} min read
                  </span>
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                  {post.title}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {post.excerpt}
                </p>
                <span
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary"
                  data-testid={`read-more-${post.slug}`}
                >
                  Read More <ArrowRight size={13} />
                </span>
              </Link>
            ))}
          </div>
        </main>

        <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} Agent9. All rights reserved.
        </footer>
      </div>
    </>
  );
}
